import { getBuildings } from "@/api/buildings/get-Buildings";
import { createInvoice } from "@/api/invoices/create-invoice";
import { queryClient } from "@/lib/reactQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import {
  useSelectedState,
  useTempSelectedBuildingName,
} from "@/components/buildings/buildingStates";
import { ImageUploadBox } from "@/components/Image";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ListPlus,
  Plus,
  Save,
  Send,
  Settings,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EditCompanyOnInvoice from "@/components/invoices/editCompanyInvoices";
import PickDate from "@/components/invoices/pickDate";
import InvoiceRow, { Row } from "@/components/invoices/invoiceRow";
import SelectAndEditBuildingInvoice from "@/components/buildings/selectedBuildingInv";
import { useCompany } from "@/components/company/companyStates";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import DownloadButton from "@/components/invoices/downloadButton";
import { getCompany } from "@/api/company/get-Company";

export const createInvoiceSchema = z.object({
  invoiceNum: z.number().nonnegative(),
  unit: z.string().optional(),
  customField: z.string().optional(),
  totalPrice: z.number().nonnegative(),
  unitSize: z.string().optional(),
  taxes: z.number().optional(),
  poBox: z.string().optional(),
  createdAt: z.coerce.date(),
  services: z.array(
    z.object({
      serviceName: z.string().optional(),
      price: z.number().optional(),
      quantity: z.number().optional(),
    }),
  ),
});

export type CreateInvoiceType = z.infer<typeof createInvoiceSchema>;

export function CreateInvoice() {
  const { control, register, handleSubmit, reset, setValue, watch } =
    useForm<CreateInvoiceType>({
      resolver: zodResolver(createInvoiceSchema),
      defaultValues: {
        invoiceNum: undefined,
        services: [],
        totalPrice: undefined,
      },
    });

  const { mutateAsync: CreateInvoiceFn } = useMutation({
    mutationKey: ["invoices"],
    mutationFn: createInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
    },
  });

  const { company, setCompany } = useCompany();

  useEffect(() => {
    (async () => {
      try {
        const companyRes = await getCompany();
        setCompany(companyRes.company[0]); // Assuming company is an array in the response
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    })();
  }, []);

  async function handleCreateInvoice(data: CreateInvoiceType) {
    if (!selectedBuilding?.id) {
      toast.error("Please select a building");
      return;
    }

    if (!company?.id) {
      toast.error("No company registered for this account");
      return;
    }

    try {
      const formattedServices = rows.map((row) => ({
        serviceName: row.service || "",
        price: Number(row.price) || 1,
        serviceQuantity: Number(row.quantity) || 1, // Match backend field name
      }));

      const formattedData = {
        ...data,
        services: formattedServices,
        companyId: company.id,
        buildingId: selectedBuilding.id,
        totalPrice: calculateTotal(rows),
      };

      const { status } = await CreateInvoiceFn(formattedData);

      if (status === 201) {
        toast.success("Invoice created successfully");
        reset();
        setRows([
          { id: 1, service: "", quantity: undefined, price: undefined },
        ]);
      }
    } catch (err) {
      console.error("Create invoice error:", err);
      toast.error("Error creating invoice");
    }
  }

  ////////////////////// BUILDINGS ///////////////////////////////////////

  const { selectedBuilding, setSelectedBuilding } = useSelectedState();
  const { tempSelectedBuildingName } = useTempSelectedBuildingName();

  useEffect(() => {
    const fetchBuildingDetails = async () => {
      if (tempSelectedBuildingName) {
        const buildingResponse = await getBuildings(
          1,
          1,
          tempSelectedBuildingName,
        );
        const building = buildingResponse.buildings[0];
        setSelectedBuilding({
          id: building.id,
          buildingName: building.buildingName,
          address: building.address,
          buildingCity: building.buildingCity,
          buildingState: building.buildingState,
          buildingZip: building.buildingZip,
          buildingCountry: building.buildingCountry,
          letterIdBuilding: building.letterIdBuilding,
        });
      }
    };
    fetchBuildingDetails();
  }, [tempSelectedBuildingName]);

  //////////////////////////////////////////////////////////////////////

  const [showUnitInput, setShowUnitInput] = useState(false);
  const [showSizeInput, setShowSizeInput] = useState(false);

  useEffect(() => {
    const savedShowUnitInput = localStorage.getItem("showUnitInput") === "true";
    setShowUnitInput(savedShowUnitInput);

    const savedShowSizeInput = localStorage.getItem("showSizeInput") === "true";
    setShowSizeInput(savedShowSizeInput);
  }, []);

  const handleUnitInputChange = (checked: boolean) => {
    setShowUnitInput(checked);
    localStorage.setItem("showUnitInput", checked.toString());
  };

  const handleSizeInputChange = (checked: boolean) => {
    setShowSizeInput(checked);
    localStorage.setItem("showSizeInput", checked.toString());
  };

  const [showInput, setShowInput] = useState(false);

  // Rows for services
  const [rows, setRows] = useState<Row[]>([
    { id: 1, service: "", quantity: undefined, price: undefined },
  ]);

  const handleChange = (
    id: number,
    field: keyof Row,
    value: string | number,
  ) => {
    setRows((prevRows) => {
      const newRows = prevRows.map((row) =>
        row.id === id ? { ...row, [field]: value } : row,
      );
      return newRows;
    });

    // Move form value updates to a separate useEffect
  };

  const handleDelete = (id: number) => {
    setRows((prevRows) => {
      if (prevRows.length <= 1) return prevRows; // Prevent deletion of last row

      const filteredRows = prevRows.filter((row) => row.id !== id);
      return filteredRows.map((row, index) => ({
        ...row,
        id: index + 1,
      }));
    });
  };

  const handleAddRow = (e?: React.MouseEvent) => {
    e?.preventDefault(); // Prevent form submission
    const newId = Math.max(...rows.map((row) => row.id)) + 1;
    setRows([
      ...rows,
      { id: newId, service: "", quantity: undefined, price: undefined },
    ]);
  };

  const calculateTotal = (rows: Row[]) => {
    return rows.reduce((total, row) => {
      if (!row.price) return total;
      const price = row.quantity ? row.price * row.quantity : Number(row.price);
      return total + price;
    }, 0);
  };

  useEffect(() => {
    if (rows.length > 0) {
      const services = rows.map((row) => ({
        serviceName: row.service || "",
        price: row.price ? Number(row.price) : 0,
        serviceQuantity: row.quantity ? Number(row.quantity) : 0, // Match backend field name
      }));

      setValue("services", services, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false, // Add this to prevent unnecessary form triggers
      });
      setValue("totalPrice", calculateTotal(rows), {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [rows, setValue]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(handleCreateInvoice);
      }}
    >
      <div>
        {/* Header */}
        <div className="flex h-14 items-center rounded-b-2xl bg-[#16A34A] bg-opacity-40 px-10">
          <h3 className="p-5 font-Hemmers text-3xl text-black">
            Create Invoice
          </h3>
        </div>

        {/* Main Container */}
        <div className="py-8">
          <div className="mx-20 rounded-lg border border-black bg-[#FAFAFA] p-4">
            {/* Company Information Section */}
            <div className="flex items-center justify-between text-base leading-none">
              {/* Left Section: Company Info */}
              <EditCompanyOnInvoice />

              {/* Right Section: Logo Upload */}
              <ImageUploadBox />
            </div>

            {/* Divider */}
            <div className="my-4 border-t"></div>

            {/* Customer and Invoice Details Section */}
            <div className="flex justify-between">
              {/* Left Section: Customer Dropdown */}

              {/* Right Section: Invoice Details */}

              <div className="flex flex-col">
                <SelectAndEditBuildingInvoice></SelectAndEditBuildingInvoice>
                <div className="mt-3 flex items-center space-x-2">
                  {/* Conditionally render label based on checkbox state */}
                  {!showUnitInput && (
                    <label className="font-Hemmers text-black">Unit:</label>
                  )}
                  <Checkbox
                    id="showUnitInput"
                    checked={showUnitInput}
                    onCheckedChange={handleUnitInputChange}
                    className="mr-2"
                  />
                  {showUnitInput && (
                    <div className="flex items-center space-x-2">
                      <label className="font-Hemmers text-black">Unit:</label>
                      <Input
                        className="mt-1 block w-36 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                        {...register("unit")}
                      />
                    </div>
                  )}
                </div>

                {/* Size Input with Checkbox */}
                <div className="mt-4 flex items-center space-x-2">
                  {/* Conditionally render label based on checkbox state */}
                  {!showSizeInput && (
                    <label className="font-Hemmers text-black">Size:</label>
                  )}
                  <Checkbox
                    id="showSizeInput"
                    checked={showSizeInput}
                    onCheckedChange={handleSizeInputChange}
                    className="mr-2"
                  />
                  {showSizeInput && (
                    <div className="flex items-center space-x-2">
                      <label className="font-Hemmers text-black">Size:</label>
                      <Input
                        className="mt-1 block w-36 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                        {...register("unitSize")}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                {/* Invoice Number and Date */}
                <div className="flex items-center gap-x-1">
                  <p className="font-Hemmers text-base text-black">Inv:</p>
                  <Input
                    className="ml-7 mt-1 block w-[148px] rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    type="number"
                    min={0}
                    step="1"
                    {...register("invoiceNum", {
                      setValueAs: (value) =>
                        parseInt(value.replace(/^0+/, "")) || 0, // Remove leading zeros when storing value
                      valueAsNumber: true,
                    })}
                    onChange={(e) => {
                      // Get the raw number by removing leading zeros
                      const rawNum =
                        parseInt(e.target.value.replace(/^0+/, "")) || 0;

                      // Format with leading zeros (padStart adds zeros to the left)
                      const formattedNum = rawNum.toString().padStart(5, "0");

                      // Update the input value with the formatted number
                      e.target.value = formattedNum;
                    }}
                  />
                  <Input
                    value={selectedBuilding?.letterIdBuilding || ""}
                    onChange={() => {}}
                    className="ml-1 mt-1 block w-10 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                  ></Input>
                </div>

                {/* Date Picker */}
                <PickDate control={control} />
                <div className="mt-2">
                  {showInput ? (
                    <Input
                      {...register("customField")}
                      type="text"
                      placeholder="Enter custom field"
                      className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                    />
                  ) : (
                    <Button
                      className="w-64 bg-gray-100 text-black hover:bg-gray-200"
                      onClick={async () => setShowInput(true)}
                    >
                      Add Custom Field
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="my-4 border-t"></div>
            <div className="w-full overflow-x-auto rounded-lg">
              <div className="min-w-[768px]">
                <Table className="overflow-x-auto">
                  <TableHeader>
                    <TableRow className="font-Hemmers text-base">
                      <TableHead className="w-[8%]">
                        <ListPlus />
                      </TableHead>
                      <TableHead className="w-[20%]">Service</TableHead>
                      <TableHead className="w-[12%]">Quantity</TableHead>
                      <TableHead className="w-[12%]">Price</TableHead>
                      <TableHead className="w-[7%]">More</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <InvoiceRow
                        key={row.id}
                        row={row}
                        totalRows={rows.length}
                        onChange={handleChange}
                        onDelete={handleDelete}
                      />
                    ))}
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Button
                          onClick={handleAddRow}
                          className="w-full bg-transparent hover:bg-gray-200"
                        >
                          <Plus className="h-6 w-6 text-green-500 hover:text-green-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={5} className="text-right">
                        <span className="font-Hemmers text-xl text-zinc-900">
                          Total: $
                          {calculateTotal(rows).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div className="flex items-center justify-end gap-6 pb-2 pt-4 text-right">
                  <span className="flex cursor-pointer items-center gap-1 font-Hemmers text-zinc-500 hover:text-zinc-900">
                    <Settings className="size-5" />
                    Customize
                  </span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button className="bg-[#16A34A] hover:bg-[#1bc459]">
                        Generate Invoice
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-40 p-1">
                      <DownloadButton
                        formData={{
                          company: company,
                          selectedBuilding: selectedBuilding,
                          rows,
                          invoiceNum: watch("invoiceNum")?.toString(),
                          unit: watch("unit"),
                          customField: watch("customField")?.toString(),
                          unitSize: watch("unitSize"),
                          createdAt: watch("createdAt"),
                          totalPrice: watch("totalPrice"),
                        }}
                      />
                      <Button
                        type="submit"
                        onClick={(e) => {
                          e.preventDefault(); // Prevent any potential double submission
                          handleSubmit(handleCreateInvoice)(e);
                        }}
                        className="w-full justify-start bg-transparent text-zinc-900 hover:bg-gray-100"
                      >
                        <Save className="mr-2 h-4 w-4" /> Save and New
                      </Button>
                      <Button className="w-full justify-start bg-transparent text-zinc-900 hover:bg-gray-100">
                        <Send className="mr-2 h-4 w-4" /> Send
                      </Button>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
